import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import ContactMailRoundedIcon from '@material-ui/icons/ContactMailRounded';
import EmailRoundedIcon from '@material-ui/icons/EmailRounded';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import PhoneRoundedIcon from '@material-ui/icons/PhoneRounded';
import { addContact } from 'actions/ringCentral';
import clsx from 'clsx';
import strings from 'strings';
import { CandidateEntity } from 'UI/constants/entityTypes';

import { useContactInfoSectionStyles } from './ContactInfoSection.styled';
import {
  filterLinkedInUrls,
  formatEmailLabel,
  formatPhoneDisplay,
  formatPhoneLabel
} from './ResumeCard.utils';

const toAbsoluteUrl = url => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

export const ContactInfoSection = ({ candidateId, phoneDetails = [], emails = [], urls = [] }) => {
  const classes = useContactInfoSectionStyles();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const linkedInUrls = filterLinkedInUrls(urls);

  const handlePhoneClick = useCallback(
    number => () => {
      if (!number || !candidateId) return;
      dispatch(
        addContact({
          id: candidateId,
          role: CandidateEntity.id,
          phone: number,
          createdAt: new Date()
        })
      );
    },
    [dispatch, candidateId]
  );

  if (phoneDetails.length === 0 && emails.length === 0 && linkedInUrls.length === 0) return null;

  return (
    <Box mt={1.5} mb={2} className={classes.container}>
      <Box
        component="button"
        type="button"
        className={classes.header}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        aria-controls="resume-card-contact-info-content"
        aria-label={strings.resumeSearch.resumeCard.toggleContactInfo}
        data-testid="resume-card-contact-info-toggle"
      >
        <Box className={classes.headerTitle}>
          <ContactMailRoundedIcon fontSize="small" color="action" />
          <Typography className={classes.headerLabel}>
            <b>{strings.resumeSearch.resumeCard.sections.contactInfo}</b>
          </Typography>
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          className={clsx(classes.expandIcon, isExpanded && classes.expandIconOpen)}
        />
      </Box>
      <Collapse in={isExpanded} unmountOnExit>
        <Box id="resume-card-contact-info-content" data-testid="resume-card-contact-info-content">
          {phoneDetails.map((detail, index) => {
            const formattedNumber = formatPhoneDisplay(detail.number);
            return (
              <Box key={detail.number} className={classes.row}>
                <Box className={classes.rowLabel}>
                  <PhoneRoundedIcon fontSize="small" />
                  <Typography variant="body2">{formatPhoneLabel(detail, index)}</Typography>
                </Box>
                <Typography variant="body2">
                  <Link
                    href={`tel:${formattedNumber}`}
                    onClick={handlePhoneClick(formattedNumber)}
                    aria-label={strings.shared.rowActions.actions.call}
                  >
                    {formattedNumber}
                  </Link>
                </Typography>
              </Box>
            );
          })}
          {emails.map((email, index) => (
            <Box key={email} className={classes.row}>
              <Box className={classes.rowLabel}>
                <EmailRoundedIcon fontSize="small" />
                <Typography variant="body2">{formatEmailLabel(index)}</Typography>
              </Box>
              <Typography variant="body2">
                <Link href={`mailto:${email}`} aria-label={strings.shared.rowActions.actions.email}>
                  {email}
                </Link>
              </Typography>
            </Box>
          ))}
          {linkedInUrls.map(url => (
            <Box key={url} className={classes.row}>
              <Box className={classes.rowLabel}>
                <LinkedInIcon fontSize="small" />
                <Typography variant="body2">
                  {strings.resumeSearch.resumeCard.contactInfo.linkedIn}
                </Typography>
              </Box>
              <Typography variant="body2">
                <Link href={toAbsoluteUrl(url)} target="_blank" rel="noopener noreferrer">
                  {url}
                </Link>
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
